// Lead-alert bell via Web Audio (no asset needed).
// Browsers block audio until the user interacts with the page, so we unlock the
// AudioContext on the first click/keypress and replay any ring that was blocked.
const MUTE_KEY = 'lead_bell_muted';
let _ctx;
let _pending = false;
let _unlockInstalled = false;

const getCtx = () => {
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  _ctx = _ctx || new AC();
  return _ctx;
};

export const isBellMuted = () => localStorage.getItem(MUTE_KEY) === '1';
export const setBellMuted = (m) => {
  if (m) localStorage.setItem(MUTE_KEY, '1'); else localStorage.removeItem(MUTE_KEY);
  window.dispatchEvent(new CustomEvent('lead-bell-mute', { detail: { muted: !!m } }));
};

// One bell strike: fundamental + inharmonic partials with a fast attack / long decay.
function strike(ctx, t, base, gainPeak) {
  [[1, 1], [2.0, 0.55], [3.01, 0.3], [4.2, 0.15]].forEach(([mult, amp]) => {
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = 'sine';
    o.frequency.value = base * mult;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(gainPeak * amp, t + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.9 / Math.sqrt(mult));
    o.connect(g);
    g.connect(ctx.destination);
    o.start(t);
    o.stop(t + 1.0);
  });
}

function ringNow(ctx) {
  const now = ctx.currentTime;
  strike(ctx, now, 1046.5, 0.5);        // C6
  strike(ctx, now + 0.32, 1318.5, 0.5); // E6
  strike(ctx, now + 0.64, 1046.5, 0.4);
  window.dispatchEvent(new CustomEvent('lead-bell'));
}

// Returns true if the bell actually sounded, false if audio is still locked
// (it will ring automatically on the next user interaction).
export function playBell() {
  if (isBellMuted()) { window.dispatchEvent(new CustomEvent('lead-bell')); return true; }
  try {
    const ctx = getCtx();
    if (!ctx) return false;
    if (ctx.state === 'running') { ringNow(ctx); return true; }
    _pending = true;
    installUnlock();
    ctx.resume().then(() => {
      if (ctx.state === 'running' && _pending) { _pending = false; ringNow(ctx); }
    }).catch(() => {});
    window.dispatchEvent(new CustomEvent('lead-bell'));
    return false;
  } catch (_) { return false; }
}

// Short two-tone chime (used by the Support Chat alert bell) — no lead-bell events.
export function playChime() {
  try {
    const ctx = getCtx();
    if (!ctx) return;
    if (ctx.state === 'suspended') ctx.resume();
    const now = ctx.currentTime;
    [880, 1174.66].forEach((f, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = 'sine';
      o.frequency.value = f;
      const t = now + i * 0.13;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.2, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.22);
      o.connect(g);
      g.connect(ctx.destination);
      o.start(t);
      o.stop(t + 0.24);
    });
  } catch (_) { /* ignore */ }
}

export function installUnlock() {
  if (_unlockInstalled) return;
  _unlockInstalled = true;
  const unlock = () => {
    try {
      const ctx = getCtx();
      if (!ctx) return;
      ctx.resume().then(() => {
        if (ctx.state === 'running') {
          if (_pending && !isBellMuted()) { _pending = false; ringNow(ctx); }
          ['pointerdown', 'keydown', 'touchstart'].forEach(e => window.removeEventListener(e, unlock, true));
        }
      }).catch(() => {});
    } catch (_) { /* ignore */ }
  };
  ['pointerdown', 'keydown', 'touchstart'].forEach(e => window.addEventListener(e, unlock, { capture: true, passive: true }));
}

export const hasPendingBell = () => _pending;

// Per-device "sound allowed" flag — set when the user taps Allow sound in the
// notification gate (a user gesture, which is what unlocks the AudioContext).
const SOUND_OK_KEY = 'lead_bell_sound_ok';
export const isSoundEnabled = () => localStorage.getItem(SOUND_OK_KEY) === '1';
export function enableSound() {
  localStorage.setItem(SOUND_OK_KEY, '1');
  setBellMuted(false);
  _pending = false;
  const ok = playBell();
  window.dispatchEvent(new CustomEvent('lead-bell-sound-enabled'));
  return ok;
}
